import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getClientsRegistry, updateClientFields, getMyFavoriteIds, toggleFavorite, CHANNEL_LABELS, type RegistryClient,
  getContractsToRenew, prolongContractLine, terminateContractLine, addOneMonthIso, type ContractToRenew } from '../../lib/refonteApi';
import { useAuth } from '../../components/AuthProvider';
import { SkeletonRows } from '../../components/refonte/Skeleton';
import EmptyState from '../../components/refonte/EmptyState';

const STATUTS = [{ v: 'actif', l: 'Actif' }, { v: 'en_pause', l: 'En pause' }, { v: 'termine', l: 'Terminé' }];

const fmtD = (d?: string | null) => (d ? new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' }) : '—');
const daysBetween = (a: string, b: string) => Math.round((new Date(a).getTime() - new Date(b).getTime()) / 86400000);

// Registre clients — annuaire de tous les clients + infos clés. Point d'entrée du hub interne
// (CSM / manager / SDR) ; chaque ligne ouvre la fiche client complète.
const ClientsRegistryPage: React.FC = () => {
  const nav = useNavigate();
  const { profile } = useAuth();
  const canEdit = ['csm', 'admin', 'super_admin'].includes(profile?.role || '');
  const [rows, setRows] = useState<RegistryClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [filt, setFilt] = useState<'actifs' | 'sans_contrat' | 'inactifs' | 'termines' | 'tous'>('actifs');

  // Contrats à reconduire : fin dans les 30 jours, ou fin passée sans suite ni clôture (semaine 2).
  const [renew, setRenew] = useState<ContractToRenew[] | null>(null);
  const [renewEdit, setRenewEdit] = useState<{ id: string; end: string; ref: string } | null>(null);
  const [renewBusy, setRenewBusy] = useState(false);
  const [renewMsg, setRenewMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try { setRows(await getClientsRegistry()); } catch (e: any) { setErr(e.message); }
    getContractsToRenew().then(setRenew).catch(() => setRenew(null));
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const doProlong = async () => {
    if (!renewEdit) return; setRenewBusy(true); setRenewMsg(null);
    try { await prolongContractLine(renewEdit.id, renewEdit.end, renewEdit.ref || null); setRenewEdit(null); await load(); setRenewMsg('Contrat prolongé.'); }
    catch (e: any) { setRenewMsg(e.message); }
    setRenewBusy(false);
  };
  const doTerminate = async (r: ContractToRenew) => {
    if (!confirm(`${r.clientName} : clôturer ce contrat à sa date de fin (${r.endDate}) ?\nS'il n'y a plus d'autre contrat, le client passe en « terminé » et ses journées futures sortent de l'agenda (gardées de côté).`)) return;
    setRenewBusy(true); setRenewMsg(null);
    try { await terminateContractLine(r.contractId, r.endDate); await load(); setRenewMsg(`${r.clientName} : contrat clôturé.`); }
    catch (e: any) { setRenewMsg(e.message); }
    setRenewBusy(false);
  };

  // Favoris (persistés par utilisateur) — étoile par ligne, remontés dans la sidebar.
  const [favs, setFavs] = useState<Set<string>>(new Set());
  useEffect(() => { if (profile?.user_id) getMyFavoriteIds(profile.user_id).then(ids => setFavs(new Set(ids))).catch(() => {}); }, [profile?.user_id]);
  const toggleFav = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!profile?.user_id) return;
    const on = !favs.has(id);
    setFavs(s => { const n = new Set(s); on ? n.add(id) : n.delete(id); return n; });
    await toggleFavorite(profile.user_id, id, on).catch(() => {});
    window.dispatchEvent(new Event('favorites-changed'));
  };

  // Repère « en production » : dernière journée planifiée dans les 30 derniers jours (ou à venir).
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const prod = (r: RegistryClient) => (r.lastProspection ? (daysBetween(today, r.lastProspection) <= 30 ? 'ok' : 'stale') : 'none');
  // Client « actif » sans contrat qui couvre aujourd'hui : à poser ou à passer en terminé (nettoyage 27 vs 37, CODIR 02/11).
  const noContract = (r: RegistryClient) => cat(r) === 'actifs' && r.hasActiveContract === false;
  const changeStatut = async (e: React.MouseEvent | React.ChangeEvent, id: string, v: string) => {
    e.stopPropagation();
    setRows(rs => rs.map(r => (r.id === id ? { ...r, statut: v } : r)));
    await updateClientFields(id, { statut: v }).catch(() => {});
  };

  // Catégorie basée sur le statut du client (actif / en pause / terminé).
  const cat = (r: RegistryClient): 'actifs' | 'inactifs' | 'termines' => {
    const s = r.statut || 'actif';
    if (s === 'termine') return 'termines';
    if (s === 'actif') return 'actifs';
    return 'inactifs'; // en_pause, inactif
  };
  const counts = { sans_contrat: rows.filter(noContract).length, actifs: rows.filter(r => cat(r) === 'actifs').length, inactifs: rows.filter(r => cat(r) === 'inactifs').length, termines: rows.filter(r => cat(r) === 'termines').length };
  const filtered = rows.filter(r =>
    (filt === 'tous' || (filt === 'sans_contrat' ? noContract(r) : cat(r) === filt)) &&
    (!q || `${r.name} ${r.company_name ?? ''}`.toLowerCase().includes(q.toLowerCase())));

  return (
    <div>
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
        <h1 className="text-[21px] font-bold tracking-tight">Registre clients</h1>
        <span className="font-mono text-[11px] text-faint">{rows.length} clients</span>
      </div>
      <p className="text-dim text-[13px] mb-4">Clients actifs par défaut. Clique une ligne pour la fiche complète (contrat, calibrage, RDV, fichiers, santé).</p>

      {err && <EmptyState tone="error" title="Chargement impossible" hint={err} className="mb-4" />}

      {canEdit && renew && renew.length > 0 && (
        <details open className="bg-panel border border-st-warn/40 rounded-card p-4 mb-4">
          <summary className="cursor-pointer text-[13px] font-semibold">
            À reconduire · {renew.filter(r => r.joursRestants < 0).length} expiré{renew.filter(r => r.joursRestants < 0).length > 1 ? 's' : ''}, {renew.filter(r => r.joursRestants >= 0).length} dans les 30 jours
            <span className="font-normal text-dim"> · prolonger (avec l'accord) ou clôturer, plus aucun contrat n'expire en silence</span>
          </summary>
          {renewMsg && <p className="mt-2 text-[12px] text-dim">{renewMsg}</p>}
          <div className="divide-y divide-line mt-2">
            {renew.map(r => (
              <div key={r.contractId} className="py-2 flex items-center gap-2.5 flex-wrap">
                <button onClick={() => nav(`/app/clients/${r.clientId}`)} className="text-[13px] font-semibold hover:underline">{r.clientName}</button>
                <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-raise text-dim">{CHANNEL_LABELS[r.channel || ''] || r.channel || '—'}{r.label ? ` · ${r.label}` : ''}</span>
                <span className="font-mono text-[11px] text-dim">{r.monthly.toLocaleString('fr-FR')} €/mois{r.days ? ` · ${r.days} j` : ''}</span>
                <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded ${r.joursRestants < 0 ? 'bg-st-bad/10 text-st-bad' : 'bg-st-warn/10 text-st-warn'}`}>
                  {r.joursRestants < 0 ? `expiré depuis ${-r.joursRestants} j` : r.joursRestants === 0 ? 'finit aujourd\'hui' : `finit dans ${r.joursRestants} j`} ({fmtD(r.endDate)})
                </span>
                <span className="ml-auto flex items-center gap-2">
                  <button disabled={renewBusy} onClick={() => setRenewEdit({ id: r.contractId, end: addOneMonthIso(r.endDate), ref: '' })} className="font-mono text-[10.5px] font-semibold text-hook-rose hover:underline">prolonger</button>
                  <button disabled={renewBusy} onClick={() => doTerminate(r)} className="font-mono text-[10.5px] text-st-bad hover:underline">terminé</button>
                </span>
                {renewEdit?.id === r.contractId && (
                  <div className="w-full flex flex-wrap items-end gap-2 mt-1.5 p-2.5 rounded-lg bg-panel-2 border border-line-2">
                    <label className="flex flex-col gap-1"><span className="font-mono text-[9px] uppercase text-faint">Nouvelle fin</span><input type="date" value={renewEdit.end} onChange={e => setRenewEdit({ ...renewEdit, end: e.target.value })} className="rounded-md border border-line-2 bg-panel px-2 py-1 text-[12.5px]" /></label>
                    <label className="flex flex-col gap-1 flex-1 min-w-[220px]"><span className="font-mono text-[9px] uppercase text-faint">Accord (devis, avenant, mail…)</span><input value={renewEdit.ref} onChange={e => setRenewEdit({ ...renewEdit, ref: e.target.value })} placeholder="ex. mail du client du 03/11" className="rounded-md border border-line-2 bg-panel px-2 py-1 text-[12.5px]" /></label>
                    <button disabled={renewBusy || !renewEdit.end} onClick={doProlong} className="text-[12px] font-semibold px-3 py-1.5 rounded-md bg-hook-rose text-white disabled:opacity-40">Prolonger</button>
                    <button onClick={() => setRenewEdit(null)} className="text-[12px] px-3 py-1.5 rounded-md border border-line-2">Annuler</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </details>
      )}

      <div className="bg-panel border border-line rounded-card p-5">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher un client…" className="rounded-md border border-line-2 bg-panel px-2.5 py-1.5 text-[13px] w-64" />
          {([['actifs', `Actifs (${counts.actifs})`], ...(counts.sans_contrat ? [['sans_contrat', `⚠ Actifs sans contrat (${counts.sans_contrat})`] as const] : []), ['inactifs', `Inactifs (${counts.inactifs})`], ['termines', `Terminés (${counts.termines})`], ['tous', 'Tous']] as const).map(([v, l]) => (
            <button key={v} onClick={() => setFilt(v)} className={`text-[11.5px] font-semibold px-2.5 py-1 rounded-md border ${filt === v ? 'bg-ink text-white border-ink' : 'border-line-2 text-dim hover:border-ink'}`}>{l}</button>
          ))}
          <span className="ml-auto font-mono text-[11px] text-faint">{filtered.length} affichés</span>
        </div>

        {loading ? <SkeletonRows count={10} /> : filtered.length === 0 ? (
          <EmptyState title="Aucun client" hint="Aucun client ne correspond au filtre." />
        ) : (
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-line-2 font-mono text-[9.5px] uppercase tracking-widest text-faint">
                  <th className="py-2 px-2 font-semibold">Client</th>
                  <th className="py-2 px-2 font-semibold">Offre</th>
                  <th className="py-2 px-2 font-semibold hidden sm:table-cell">Jours/mois</th>
                  <th className="py-2 px-2 font-semibold">Statut</th>
                  <th className="py-2 px-2 font-semibold">Calibrage</th>
                  <th className="py-2 px-2 font-semibold whitespace-nowrap hidden lg:table-cell" title="RDV bookés / honorés">RDV</th>
                  <th className="py-2 px-2 font-semibold whitespace-nowrap hidden lg:table-cell" title="Réponses mail · LinkedIn">Mail · LI</th>
                  <th className="py-2 px-2 font-semibold whitespace-nowrap hidden md:table-cell">Dern. prospection</th>
                  <th className="py-2 px-2 font-semibold">Santé</th>
                  <th className="py-2 px-2 w-6"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {filtered.map(r => (
                  <tr key={r.id} onClick={() => nav(`/app/clients/${r.id}`)} className="cursor-pointer hover:bg-panel-2 transition-colors">
                    <td className="py-2.5 px-2">
                      <div className="flex items-center gap-1.5">
                        <button onClick={e => toggleFav(e, r.id)} className={`text-[14px] leading-none ${favs.has(r.id) ? 'text-amber-400' : 'text-line-2 hover:text-amber-400'}`} title={favs.has(r.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}>{favs.has(r.id) ? '★' : '☆'}</button>
                        <div className="text-[13px] font-semibold">{r.name}</div>
                        {noContract(r) && <span className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-st-warn/10 text-st-warn" title="Statut actif mais aucun contrat ne couvre aujourd'hui">sans contrat</span>}
                      </div>
                      {r.company_name && r.company_name !== r.name && <div className="text-[11px] text-faint ml-5">{r.company_name}</div>}
                    </td>
                    <td className="py-2.5 px-2">
                      {r.offers.length ? (
                        <span className="inline-flex flex-wrap gap-1">
                          {r.offers.map(o => <span key={o} className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-raise text-dim">{CHANNEL_LABELS[o] || o}</span>)}
                        </span>
                      ) : <span className="text-[12px] text-faint" title="Aucun contrat actif">—</span>}
                    </td>
                    <td className="py-2.5 px-2 font-mono text-[12px] text-dim hidden sm:table-cell">{r.days_per_month ?? '—'}</td>
                    <td className="py-2.5 px-2" onClick={e => e.stopPropagation()}>
                      {canEdit ? (
                        <select value={r.statut || 'actif'} onChange={e => changeStatut(e, r.id, e.target.value)} className="rounded-md border border-line-2 bg-panel px-1.5 py-1 text-[11.5px]">
                          {STATUTS.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
                        </select>
                      ) : <span className="text-[12px] text-dim">{STATUTS.find(s => s.v === (r.statut || 'actif'))?.l}</span>}
                    </td>
                    <td className="py-2.5 px-2">{r.calibrageDone ? <span className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-st-ok/10 text-st-ok">✓ fait</span> : <span className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-raise text-faint">à faire</span>}</td>
                    <td className="py-2.5 px-2 font-mono text-[11.5px] tabular-nums whitespace-nowrap hidden lg:table-cell">
                      {r.rdvBooked ? <span className="text-dim">{r.rdvBooked} <span className="text-faint">booké{r.rdvBooked > 1 ? 's' : ''}</span> · <span className="text-ink">{r.rdvHeld}</span> <span className="text-faint">hon.</span></span> : <span className="text-faint">—</span>}
                    </td>
                    <td className="py-2.5 px-2 font-mono text-[11.5px] tabular-nums whitespace-nowrap hidden lg:table-cell">
                      {(r.mailReplies || r.liReplies || r.mailCampaigns || r.liCampaigns)
                        ? <span className="text-dim">{r.mailReplies || 0}<span className="text-faint">✉</span> · {r.liReplies || 0}<span className="text-faint">in</span></span>
                        : <span className="text-faint">—</span>}
                    </td>
                    <td className="py-2.5 px-2 font-mono text-[11.5px] text-dim tabular-nums whitespace-nowrap hidden md:table-cell">{fmtD(r.lastProspection)}</td>
                    <td className="py-2.5 px-2">
                      <span className={`inline-flex items-center gap-1.5 font-mono text-[10px] ${prod(r) === 'ok' ? 'text-st-ok' : prod(r) === 'stale' ? 'text-st-warn' : 'text-faint'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${prod(r) === 'ok' ? 'bg-st-ok' : prod(r) === 'stale' ? 'bg-st-warn' : 'bg-line-2'}`} />
                        {prod(r) === 'ok' ? 'en prod' : prod(r) === 'stale' ? 'inactif' : '—'}
                      </span>
                    </td>
                    <td className="py-2.5 px-2 font-mono text-[11px] text-faint text-right">›</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientsRegistryPage;
